/**
 * Error Message Parser
 * Parses ErrorResponseDto from the API and generates user-friendly messages
 * with optional action components based on error code and entity context.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ErrorResponseDto, ExceptionCodeEnum } from "@/types/api/common";

// =============================================================================
// ERROR CODE TO DISPLAY MESSAGE MAP
// =============================================================================

/**
 * Configuration for generating error display messages
 */
interface ErrorMessageConfig {
    /** Base message to display (may include {entityType} and {entityId} placeholders) */
    message: string;
    /** Whether this error type supports linking to the related entity */
    supportsEntityLink?: boolean;
    /** Custom message generator when entity context is available */
    withEntityMessage?: (entityType: string, entityId: number, errorResponse?: ErrorResponseDto) => string;
}

/**
 * Map of exception codes to their display message configurations
 */
const ERROR_MESSAGE_MAP: Record<string, ErrorMessageConfig> = {
    // ==========================================================================
    // Authentication Errors (1xx)
    // ==========================================================================
    [ExceptionCodeEnum.AuthTokenExpired]: {
        message: "Your session has expired. Please log in again.",
    },
    [ExceptionCodeEnum.AuthTokenInvalid]: {
        message: "Your authentication token is invalid. Please log in again.",
    },
    [ExceptionCodeEnum.AuthMissingClaims]: {
        message: "Your account is missing required permissions. Please contact support.",
    },
    [ExceptionCodeEnum.AuthSignatureInvalid]: {
        message: "Authentication failed. Please log in again.",
    },

    // ==========================================================================
    // Authorization Errors (2xx)
    // ==========================================================================
    [ExceptionCodeEnum.AuthzAccessDenied]: {
        message: "You do not have permission to perform this action.",
        supportsEntityLink: true,
        withEntityMessage: (entityType, entityId) => `You do not have permission to access this ${entityType.toLowerCase()} (ID: ${entityId}).`,
    },
    [ExceptionCodeEnum.AuthzTenantMismatch]: {
        message: "This resource belongs to a different organization.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.AuthzOrganizationMismatch]: {
        message: "This resource belongs to a different organization.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.AuthzInsufficientRole]: {
        message: "Your user role does not permit this operation.",
    },
    [ExceptionCodeEnum.AuthzEntitlementDenied]: {
        message: "This feature is not included in your current plan. Please upgrade to access this feature.",
    },

    // ==========================================================================
    // Validation Errors (3xx)
    // ==========================================================================
    [ExceptionCodeEnum.ValMissingRequiredField]: {
        message: "Please fill in all required fields.",
    },
    [ExceptionCodeEnum.ValInvalidFormat]: {
        message: "One or more fields have an invalid format.",
    },
    [ExceptionCodeEnum.ValMaxLengthExceeded]: {
        message: "One or more fields exceed the maximum allowed length.",
    },
    [ExceptionCodeEnum.ValBelowMinimum]: {
        message: "One or more values are below the minimum allowed.",
    },
    [ExceptionCodeEnum.ValAboveMaximum]: {
        message: "One or more values exceed the maximum allowed.",
    },
    [ExceptionCodeEnum.ValFileTooLarge]: {
        message: "The file size exceeds the allowed limit.",
    },
    [ExceptionCodeEnum.ValUnsupportedFileType]: {
        message: "This file type is not supported.",
    },
    [ExceptionCodeEnum.ValFileTypeNotEnabled]: {
        message: "This file format is not enabled for your organization.",
    },
    [ExceptionCodeEnum.ValInvalidId]: {
        message: "Invalid ID format provided.",
    },
    [ExceptionCodeEnum.ValInvalidDateTime]: {
        message: "Invalid date or time format.",
    },
    [ExceptionCodeEnum.ValInvalidEnumValue]: {
        message: "Invalid value provided.",
    },
    [ExceptionCodeEnum.ValInvalidOrganizationFormat]: {
        message: "Invalid organization name format.",
    },
    ValRuleExpressionFailed: {
        message: "A workflow rule expression could not be evaluated.",
    },
    ValFieldValidationFailed: {
        message: "A field validation rule failed.",
    },
    ValAggregationMismatch: {
        message: "An aggregation calculation mismatch was detected.",
    },

    // ==========================================================================
    // Not Found Errors (4xx)
    // ==========================================================================
    [ExceptionCodeEnum.NotFoundDocument]: {
        message: "The document you requested could not be found.",
        supportsEntityLink: true,
        withEntityMessage: (_, entityId) => `Document #${entityId} could not be found.`,
    },
    [ExceptionCodeEnum.NotFoundFile]: {
        message: "The file you requested could not be found.",
        supportsEntityLink: true,
        withEntityMessage: (_, entityId) => `File #${entityId} could not be found.`,
    },
    [ExceptionCodeEnum.NotFoundJob]: {
        message: "The job you requested could not be found.",
        supportsEntityLink: true,
        withEntityMessage: (_, entityId) => `Job #${entityId} could not be found.`,
    },
    [ExceptionCodeEnum.NotFoundUser]: {
        message: "The user could not be found.",
    },
    [ExceptionCodeEnum.NotFoundOrganization]: {
        message: "The organization could not be found.",
    },
    [ExceptionCodeEnum.NotFoundClient]: {
        message: "The client could not be found.",
    },
    [ExceptionCodeEnum.NotFoundDocumentType]: {
        message: "The document type could not be found.",
    },
    [ExceptionCodeEnum.NotFoundRule]: {
        message: "The rule could not be found.",
    },
    [ExceptionCodeEnum.NotFoundWorkflow]: {
        message: "The workflow could not be found.",
    },
    [ExceptionCodeEnum.NotFoundCustomField]: {
        message: "The custom field definition could not be found.",
    },
    [ExceptionCodeEnum.NotFoundBlobContent]: {
        message: "The file content could not be found in storage.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.NotFoundException]: {
        message: "The requested resource could not be found.",
    },
    [ExceptionCodeEnum.NotFoundPromptTemplate]: {
        message: "The prompt template could not be found.",
    },
    [ExceptionCodeEnum.NotFoundEntitlement]: {
        message: "The entitlement could not be found.",
    },

    // ==========================================================================
    // Business Logic Errors (5xx)
    // ==========================================================================
    [ExceptionCodeEnum.BizDuplicateFile]: {
        message: "This file has already been uploaded.",
        supportsEntityLink: true,
        withEntityMessage: (entityType, _entityId) => {
            if (entityType.toLowerCase() === "file") {
                return `This file has already been uploaded. See the`;
            }
            return "This file has already been uploaded.";
        },
    },
    [ExceptionCodeEnum.BizDuplicateDocument]: {
        message: "This document already exists.",
        supportsEntityLink: true,
        withEntityMessage: (entityType, _entityId) => {
            if (entityType.toLowerCase() === "document") {
                return `This document already exists. See the`;
            }
            return "This document already exists.";
        },
    },
    [ExceptionCodeEnum.BizToleranceExceeded]: {
        message: "The values exceed the allowed tolerance limits.",
    },
    [ExceptionCodeEnum.BizExportBlocked]: {
        message: "Export is blocked due to unresolved exceptions. Please resolve all issues before exporting.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.BizConcurrentUpdateConflict]: {
        message: "Another user changed this document since you loaded it. Reload to see the latest values.",
    },
    [ExceptionCodeEnum.BizInvalidStateTransition]: {
        message: "This status change is not allowed.",
        supportsEntityLink: true,
        withEntityMessage: (entityType, entityId) => `Cannot change the status of ${entityType.toLowerCase()} #${entityId}. This transition is not allowed.`,
    },
    [ExceptionCodeEnum.BizResolutionNoteRequired]: {
        message: "A resolution note is required to proceed.",
    },
    [ExceptionCodeEnum.BizMappingFailure]: {
        message: "The mapping could not be completed.",
    },
    [ExceptionCodeEnum.BizMatchFailure]: {
        message: "No matching records were found.",
    },
    [ExceptionCodeEnum.BizVendorNotFound]: {
        message: "No matching vendor was found in the ERP system.",
    },
    [ExceptionCodeEnum.BizEntityDeleted]: {
        message: "This item has been deleted.",
        supportsEntityLink: true,
        withEntityMessage: (entityType, entityId) => `The ${entityType.toLowerCase()} #${entityId} has been deleted.`,
    },
    [ExceptionCodeEnum.BizMaxRetriesExceeded]: {
        message: "Maximum retry attempts exceeded. Please contact support.",
    },
    [ExceptionCodeEnum.BizQuotaExceeded]: {
        message: "You have reached your monthly page quota limit. Please upgrade your plan or wait until next month to upload more files.",
        withEntityMessage: (_, __, errorResponse) => {
            // Use additional quota fields if available for more detailed message
            if (errorResponse?.remainingQuota !== undefined && errorResponse?.requestedPages !== undefined) {
                return `Page quota exceeded. Remaining: ${errorResponse.remainingQuota}, Requested: ${errorResponse.requestedPages}. Please upgrade your plan or wait until next month.`;
            }
            return "You have reached your monthly page quota limit. Please upgrade your plan or wait until next month to upload more files.";
        },
    },

    // ==========================================================================
    // Processing Errors (6xx)
    // ==========================================================================
    [ExceptionCodeEnum.ProcOcrFailed]: {
        message: "Text extraction (OCR) failed. Please try uploading the file again.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcOcrEmptyResult]: {
        message: "No text could be extracted from this file. Please ensure the file contains readable text.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcClassificationFailed]: {
        message: "Document classification failed. Please try again or contact support.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcClassificationEmptyResult]: {
        message: "No documents could be identified in this file.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcClassificationUnknownType]: {
        message: "The document type could not be recognized. Please verify the document is a supported type.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcClassificationValidationFailed]: {
        message: "Document classification validation failed. Try reprocessing the file.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcParseFailed]: {
        message: "Data extraction failed. Please try again or contact support.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcParseEmptyResult]: {
        message: "No data could be extracted from this document.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcMatchFailed]: {
        message: "Document matching failed. Please try again.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcPrerequisiteNotMet]: {
        message: "A prerequisite step has not been completed.",
    },
    [ExceptionCodeEnum.ProcUnsupportedJobType]: {
        message: "This job type is not supported.",
    },
    [ExceptionCodeEnum.ProcJobDeserializationFailed]: {
        message: "Failed to process the job. Please contact support.",
    },
    [ExceptionCodeEnum.ProcStatusUpdateFailed]: {
        message: "Failed to update the status. Please try again.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcExtractionUncertain]: {
        message: "Some extracted data has low confidence. Please review and verify.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcDocumentCreationFailed]: {
        message: "Failed to create documents. Please try again or contact support.",
        supportsEntityLink: true,
    },
    [ExceptionCodeEnum.ProcQuotaCheckFailed]: {
        message: "Failed to check upload quota. Please try again later.",
    },
    [ExceptionCodeEnum.ProcUsageTrackingFailed]: {
        message: "Failed to track usage. Please contact support if this issue persists.",
    },
    [ExceptionCodeEnum.ProcCalculatedFieldFailed]: {
        message: "Failed to evaluate calculated field expression.",
        supportsEntityLink: true,
    },

    // ==========================================================================
    // Integration Errors (7xx)
    // ==========================================================================
    [ExceptionCodeEnum.IntOpenAiFailed]: {
        message: "AI processing service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntOpenAiInvalidResponse]: {
        message: "AI processing returned an unexpected response. Please try again.",
    },
    [ExceptionCodeEnum.IntAirTableFailed]: {
        message: "Data sync service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntBlobStorageFailed]: {
        message: "File storage service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntVercelBlobFailed]: {
        message: "File storage service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntHttpRequestFailed]: {
        message: "An external service request failed. Please try again later.",
    },
    [ExceptionCodeEnum.IntRateLimited]: {
        message: "Too many requests. Please wait a moment and try again.",
    },
    [ExceptionCodeEnum.IntTimeout]: {
        message: "The request timed out. Please try again.",
    },
    [ExceptionCodeEnum.IntServiceError]: {
        message: "An external service error occurred. Please try again later.",
    },
    [ExceptionCodeEnum.IntClerkFailed]: {
        message: "Authentication service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.IntClerkSyncFailed]: {
        message: "Failed to synchronize account information. Please try again later.",
    },

    // ==========================================================================
    // Infrastructure Errors (8xx)
    // ==========================================================================
    [ExceptionCodeEnum.InfDatabaseError]: {
        message: "A database error occurred. Please try again later.",
    },
    [ExceptionCodeEnum.InfQueueError]: {
        message: "A processing queue error occurred. Please try again later.",
    },
    [ExceptionCodeEnum.InfConfigurationMissing]: {
        message: "System configuration error. Please contact support.",
    },
    [ExceptionCodeEnum.InfServiceBusError]: {
        message: "A messaging service error occurred. Please try again later.",
    },
    [ExceptionCodeEnum.InfServiceUnavailable]: {
        message: "The service is temporarily unavailable. Please try again later.",
    },
    [ExceptionCodeEnum.InfSerializationError]: {
        message: "A data processing error occurred. Please contact support.",
    },
    [ExceptionCodeEnum.InfClientSettingsInvalid]: {
        message: "Client settings are not properly configured. Please contact support.",
    },
    [ExceptionCodeEnum.InfSchemaInvalid]: {
        message: "A data schema error occurred. Please contact support.",
    },

    // ==========================================================================
    // Unknown Error
    // ==========================================================================
    [ExceptionCodeEnum.Unknown]: {
        message: "An unexpected error occurred. Please try again or contact support.",
    },
};

/**
 * Default error message when no specific message is configured
 */
const DEFAULT_ERROR_MESSAGE = "An unexpected error occurred. Please try again.";

// =============================================================================
// ENTITY ROUTE MAP
// =============================================================================

/**
 * Map of entity types to their route patterns
 * Uses lowercase keys for case-insensitive matching
 */
const ENTITY_ROUTE_MAP: Record<string, (entityId: number) => string> = {
    document: (id) => `/legacy-documents?id=${id}`,
    file: (id) => `/legacy-files?id=${id}`,
    job: (id) => `/jobs/${id}`,
    lineitem: (id) => `/line-items/${id}`,
    user: (id) => `/users/${id}`,
    organization: (id) => `/organizations/${id}`,
    client: (id) => `/clients/${id}`,
};

// =============================================================================
// PARSED ERROR RESULT
// =============================================================================

/**
 * Result of parsing an error response
 */
export interface ParsedErrorResult {
    /** User-friendly display message */
    displayMessage: string;
    /** Optional React component for action (e.g., link to entity) */
    actionComponent: React.ReactNode;
    /** The error code from the response */
    code?: string;
    /** Numeric error code for programmatic handling */
    numericCode?: number;
    /** Exception ID for support tickets */
    exceptionId?: number;
    /** Related entity ID if applicable */
    entityId?: number;
    /** Related entity type if applicable */
    entityType?: string;
    /** Route to the related entity if applicable */
    entityRoute?: string;
}

// =============================================================================
// MAIN PARSER FUNCTIONS
// =============================================================================

/**
 * Get the display message for an error code
 *
 * @param code - The exception code from the API
 * @param entityType - Optional entity type for context-aware messages
 * @param entityId - Optional entity ID for context-aware messages
 * @param errorResponse - Optional full error response for additional context (e.g., quota fields)
 * @returns User-friendly error message
 */
export function getErrorDisplayMessage(code: string | undefined, entityType?: string, entityId?: number, errorResponse?: ErrorResponseDto): string {
    if (!code) {
        return DEFAULT_ERROR_MESSAGE;
    }

    const config = ERROR_MESSAGE_MAP[code];

    if (!config) {
        return DEFAULT_ERROR_MESSAGE;
    }

    // Special handling for quota exceeded errors with additional fields
    if (code === ExceptionCodeEnum.BizQuotaExceeded && errorResponse) {
        if (errorResponse.remainingQuota !== undefined && errorResponse.requestedPages !== undefined) {
            return `Page quota exceeded. Remaining: ${errorResponse.remainingQuota}, Requested: ${errorResponse.requestedPages}. Please upgrade your plan or wait until next month.`;
        }
    }

    // If entity context is available and the error supports custom messages
    if (entityType && entityId && config.withEntityMessage) {
        return config.withEntityMessage(entityType, entityId, errorResponse);
    }

    return config.message;
}

/**
 * Get the route to navigate to the related entity
 *
 * @param entityType - The entity type
 * @param entityId - The entity ID
 * @returns Route string or null if not applicable
 */
export function getEntityRoute(entityType: string | undefined, entityId: number | undefined): string | null {
    if (!entityType || !entityId) {
        return null;
    }

    // Use lowercase for case-insensitive matching
    const normalizedType = entityType.toLowerCase().replace(/\s+/g, "");
    const routeGenerator = ENTITY_ROUTE_MAP[normalizedType];
    return routeGenerator ? routeGenerator(entityId) : null;
}

/**
 * Generate an action component (link) based on the error response
 *
 * @param errorResponse - The full error response from the API
 * @returns React component for the action or null
 */
export function getErrorActionComponent(errorResponse: ErrorResponseDto): React.ReactNode {
    const { code, entityType, entityId } = errorResponse;

    if (!code || !entityType || !entityId) {
        return null;
    }

    const config = ERROR_MESSAGE_MAP[code];

    // Only generate links for errors that support entity linking
    if (!config?.supportsEntityLink) {
        return null;
    }

    const route = getEntityRoute(entityType, entityId);

    if (!route) {
        return null;
    }

    // Generate appropriate link text based on error code and entity type
    const linkText = getLinkText(code, entityType);

    return (
        <Link to={route} className="hover:text-brand-primary-hover text-sm font-medium text-brand-primary underline">
            {linkText}
        </Link>
    );
}

/**
 * Get appropriate link text based on error code and entity type
 */
function getLinkText(code: string, entityType: string): string {
    // Special cases for duplicate errors
    if (code === ExceptionCodeEnum.BizDuplicateFile) {
        return "existing file";
    }
    if (code === ExceptionCodeEnum.BizDuplicateDocument) {
        return "existing document";
    }

    // Default: use entity type
    const entityTypeLower = entityType.toLowerCase();
    return `View ${entityTypeLower}`;
}

/**
 * Parse an ErrorResponseDto and return a comprehensive result
 *
 * @param errorResponse - The error response from the API
 * @returns ParsedErrorResult with display message and action component
 */
export function parseErrorResponse(errorResponse: ErrorResponseDto): ParsedErrorResult {
    const { error, code, numericCode, exceptionId, entityId, entityType, message } = errorResponse;

    // Get display message - prefer code-based message, fall back to raw message
    const displayMessage = code ? getErrorDisplayMessage(code, entityType, entityId, errorResponse) : message || error || DEFAULT_ERROR_MESSAGE;

    // Generate action component if applicable
    const actionComponent = getErrorActionComponent(errorResponse);

    // Get entity route if applicable
    const entityRoute = getEntityRoute(entityType, entityId) || undefined;

    return {
        displayMessage,
        actionComponent,
        code,
        numericCode,
        exceptionId,
        entityId,
        entityType,
        entityRoute,
    };
}

/**
 * Check if an error response indicates a duplicate file error
 */
export function isDuplicateFileError(errorResponse: ErrorResponseDto): boolean {
    return errorResponse.code === ExceptionCodeEnum.BizDuplicateFile;
}

/**
 * Check if an error response indicates a duplicate document error
 */
export function isDuplicateDocumentError(errorResponse: ErrorResponseDto): boolean {
    return errorResponse.code === ExceptionCodeEnum.BizDuplicateDocument;
}

/**
 * Check if an error response indicates an authentication error
 */
export function isAuthenticationError(errorResponse: ErrorResponseDto): boolean {
    const numericCode = errorResponse.numericCode;
    return numericCode !== undefined && numericCode >= 100 && numericCode < 200;
}

/**
 * Check if an error response indicates an authorization error
 */
export function isAuthorizationError(errorResponse: ErrorResponseDto): boolean {
    const numericCode = errorResponse.numericCode;
    return numericCode !== undefined && numericCode >= 200 && numericCode < 300;
}

/**
 * Check if an error response indicates a validation error
 */
export function isValidationError(errorResponse: ErrorResponseDto): boolean {
    const numericCode = errorResponse.numericCode;
    return numericCode !== undefined && numericCode >= 300 && numericCode < 400;
}

/**
 * Check if an error response indicates a not found error
 */
export function isNotFoundError(errorResponse: ErrorResponseDto): boolean {
    const numericCode = errorResponse.numericCode;
    return numericCode !== undefined && numericCode >= 400 && numericCode < 500;
}

/**
 * Check if an error response indicates a business logic error
 */
export function isBusinessError(errorResponse: ErrorResponseDto): boolean {
    const numericCode = errorResponse.numericCode;
    return numericCode !== undefined && numericCode >= 500 && numericCode < 600;
}

/**
 * Check if an error response indicates a processing error
 */
export function isProcessingError(errorResponse: ErrorResponseDto): boolean {
    const numericCode = errorResponse.numericCode;
    return numericCode !== undefined && numericCode >= 600 && numericCode < 700;
}

/**
 * Check if the error is retryable (integration or infrastructure errors)
 */
export function isRetryableError(errorResponse: ErrorResponseDto): boolean {
    const numericCode = errorResponse.numericCode;
    if (numericCode === undefined) return false;

    // Integration (7xx) and Infrastructure (8xx) errors are generally retryable
    return numericCode >= 700 && numericCode < 900;
}

// =============================================================================
// LEGACY SUPPORT (Backward compatibility with old string-based approach)
// =============================================================================

/**
 * @deprecated Use parseErrorResponse instead
 * Legacy error message delimiter
 */
const ERROR_DELIMITER = "###";

/**
 * @deprecated Use parseErrorResponse instead
 * Legacy parsed error message structure
 */
export interface ParsedErrorMessage {
    errorMessage: string;
    data: string;
}

/**
 * @deprecated Use parseErrorResponse instead
 * Parse a legacy error message string in the format "<ErrorMessage>###<Something>"
 */
export function parseErrorMessage(errorString: string): ParsedErrorMessage {
    if (!errorString) {
        return { errorMessage: "", data: "" };
    }

    const delimiterIndex = errorString.indexOf(ERROR_DELIMITER);

    if (delimiterIndex === -1) {
        return { errorMessage: errorString, data: "" };
    }

    const errorMessage = errorString.substring(0, delimiterIndex);
    const data = errorString.substring(delimiterIndex + ERROR_DELIMITER.length);

    return { errorMessage, data };
}

/**
 * @deprecated Use parseErrorResponse instead
 * Get the display error message from a legacy string
 */
export function getDisplayErrorMessage(errorString: string): string {
    const { errorMessage } = parseErrorMessage(errorString);
    return errorMessage || errorString;
}

/**
 * @deprecated Use getErrorActionComponent with ErrorResponseDto instead
 * Generate an action component based on legacy error message and data
 */
export function getLegacyErrorActionComponent(errorMessage: string, data: string): React.ReactNode {
    if (!data || data.trim() === "") {
        return null;
    }

    const normalizedMessage = errorMessage.toLowerCase().trim();

    switch (normalizedMessage) {
        case "duplicate file": {
            const fileId = parseInt(data, 10);
            if (isNaN(fileId)) {
                console.warn("[ErrorActionComponent] Invalid file ID for duplicate file:", data);
                return null;
            }

            return (
                <Link to={`/legacy-files?id=${fileId}`} className="hover:text-brand-primary-hover text-sm font-medium text-brand-primary underline">
                    existing file
                </Link>
            );
        }

        default:
            return null;
    }
}

/**
 * @deprecated Use parseErrorResponse instead
 * Parse a legacy error string and return both display message and action component
 */
export function parseErrorWithAction(errorString: string): {
    displayMessage: string;
    actionComponent: React.ReactNode;
} {
    const { errorMessage, data } = parseErrorMessage(errorString);

    return {
        displayMessage: errorMessage || errorString,
        actionComponent: getLegacyErrorActionComponent(errorMessage, data),
    };
}

// =============================================================================
// EXPORT ERROR MESSAGE MAP FOR TESTING/EXTENSION
// =============================================================================

/**
 * Get all configured error codes and their messages
 * Useful for documentation or testing
 */
export function getErrorMessageMap(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const [code, config] of Object.entries(ERROR_MESSAGE_MAP)) {
        result[code] = config.message;
    }
    return result;
}
